
import { defineComponent } from 'vue';
import { mapGetters, mapState } from 'vuex';

import MapTilerMap from '@/components/MapTilerMap.vue';
import MapTilerSearch from '@/components/MapTilerSearch.vue';
import { ShopBase } from '~model';
import store from '~store';

export default defineComponent({
  components: {
    LoadingIndicator: () => import('@/components/widgets/LoadingIndicator.vue'),
    ShopWallItem: () => import('@/components/redeem/shop-wall/ShopWallItem.vue'),
    ShopWallFilter: () => import('@/components/redeem/shop-wall/filter/ShopWallFilter.vue'),
    MapTilerMap,
    MapTilerSearch,
  },

  props: {
    /* Voucher category ID as number. Like 1 for default Wunschgutschein or 12 on /kids variation */
    voucherCategoryId: {
      type: Number,
      required: true,
    },
    /* Value of the voucher as number like 1500 on a 15,00€ voucher */
    voucherValue: {
      type: Number,
      default: null,
    },
    /* Voucher currency as string like "EUR" */
    voucherCurrency: {
      type: String,
      default: null,
    },
    /* If interactive, shop-items are clickable */
    interactive: {
      type: Boolean,
      default: false,
    },
    /* Distribution for the shopwall-api-call like "MARKETING B2B" or "ONLINE" */
    distribution: {
      type: String,
      default: null,
    },
    region: {
      type: Boolean,
      default: false,
    },
    extraFields: {
      type: String,
      default: 'voucherValues',
    },
    showMskBanner: {
      type: Boolean,
      default: true,
    },
  },

  data() {
    return {
      // if the page already scrolled to anchor
      scrolled: false,

      limitShops: {
        // current limit for the top-shops
        top: 20,
        // current limit for the default-shops
        default: 20,
      },
      // base / fallback limit
      fallbackLimit: 20,
      scrollMultiplier: 3,
      bottomTolerance: -150, // in pixel
      store,
      eventNames: ['mousewheel', 'scroll', 'resize'],
    };
  },

  computed: {
    // Vuex map state
    ...mapState({
      // ...
      filters: (state: any) => state.filter,
    }),

    // Vuex map getters
    ...mapGetters({
      filteredTopShops: 'filteredTopShops',
      filteredDefaultShops: 'filteredDefaultShops',
      shops: 'getShopWall',
      heading: 'getWallHeadingTranslation',
      showFilterBar: 'getShowShopFilterBar',
      anyFiltersExist: 'anyFiltersExist',
      category: 'getCategory',
      country: 'getCountry',
      shopWallLoading: 'getShopWallLoading',
      showMskPromo: 'getShowMskPromo',
      configId: 'getConfigId',
      shopWall: 'getShopWall',
    }),
  },

  watch: {
    /*
     * Watch the boolean if any filter exists.
     * On change to any state, fetch the shopwall by current shopwall values.
     * (Use cached if exists or load again from api.)
     */
    anyFiltersExist() {
      this.$store.dispatch('fetchShopWall', this.shopWallValues());
    },

    /*
     * Watch on shops changing and scroll to the
     * anchor (hash-element) once.
     */
    shops() {
      if (this.scrolled === false) {
        setTimeout(() => {
          const hash = this?.$route?.hash;

          if (hash) {
            const el = document?.querySelector?.(hash);

            if (el) {
              el?.scrollIntoView?.();
            }
          }
        }, 150);
        this.scrolled = true;
      }
    },

    /*
     * On changes on the filtered top shops, reset top-shops limit.
     */
    filteredTopShops() {
      this.resetLimit('top');
    },

    /*
     * On changes on the filtered default shops, reset default-shops limit.
     */
    filteredDefaultShops() {
      this.resetLimit('default');
    },
  },

  created() {
    this.eventNames.forEach((event) => {
      window.addEventListener(event, this.handleLimitsOnChange);
    });
  },

  beforeDestroy() {
    this.eventNames.forEach((event) => {
      window.removeEventListener(event, this.handleLimitsOnChange);
    });
  },

  mounted() {
    if (this.configId === 'region') {
      this.$store.dispatch('fetchShopWall', this.shopWallValues());
    } else {
      this.$store.dispatch('fetchShopWall', this.shopWallValues('T'));
    }

    if (this.showFilterBar && this.configId !== 'region') {
      this.$store.dispatch('fetchCategories', this.voucherCategoryId);
    }
  },

  methods: {
    /*
     * Shopwall values for fetching current shopwall from API (or get from cache).
     */
    shopWallValues(position?: string) {
      return {
        id: this.voucherCategoryId,
        params: {
          distribution: this.distribution,
          voucherValue: this.voucherValue,
          currency: this.voucherCurrency,
          extraFields: this.extraFields,
          onlyWithLogo: 1,
          position,
        },
      };
    },

    /*
     * Get the shop limit
     */
    getShopLimit(key: string): number {
      // eslint-disable-next-line
      // @ts-ignore
      return this.limitShops?.[key];
    },

    /*
     * Set the shop limit to a value
     */
    setShopLimit(
      key: string,
      value: number,
    ): void {
      // eslint-disable-next-line
      // @ts-ignore
      this.limitShops[key] = value;
    },

    /*
     * Limit the array of shop-wall-items using splice
     */
    limitWallItemsArray(
      items: [],
      key: string,
    ): ShopBase[] {
      return items.slice(0, this.getShopLimit(key) ?? items.length);
    },

    /*
     * Handle limit on a change (scroll or resize)
     * calls check bottom reached on default and top shops (and refs by key)
     */
    handleLimitsOnChange() {
      this.checkBottomReached('default');
      this.checkBottomReached('top');
    },

    /*
     * Check if the wrapper-bottom position of ref is reached.
     * => then load even more items.
     */
    checkBottomReached(key: string): void {
      this.$nextTick(() => {
        // eslint-disable-next-line
        // @ts-ignore
        const wrapperBottom = this.$refs?.[key]?.getBoundingClientRect()?.bottom;
        const bottomReached = window?.innerHeight > wrapperBottom + this.bottomTolerance;

        if (bottomReached) {
          this.loadMoreItems(key);
        }
      });
    },

    /*
     * Loading more wall-items.
     * increasing the current limit for the shop by the scrollMultiplier
     */
    loadMoreItems(key: string): void {
      const newLimit = Math.round(this.getShopLimit(key) * this.scrollMultiplier ?? 2);
      this.setShopLimit(key, newLimit);
    },

    /*
     * Reset the limit to the default maximum limit
     */
    resetLimit(key: string): void {
      this.setShopLimit(key, this.fallbackLimit);
      this.handleLimitsOnChange();
    },
  },
});
